import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { css } from "@emotion/react";

import { useTheme } from "../../../components/theme/hooks";
import type { BriefBlock } from "../../schema";
import BlockEditor from "../blocks/BlockEditor";
import type { BlockAutoFocus, BlockControls } from "../blocks/types";

type SortableBlockProps = {
    block: BriefBlock;
    previousBlock: BriefBlock | undefined;
    onChange: (block: BriefBlock) => void;
    controls: BlockControls;
    autoFocus: BlockAutoFocus;
    listMarker: string;
};

export default function SortableBlock(props: SortableBlockProps) {
    const { block, previousBlock, onChange, controls, autoFocus, listMarker } = props;
    const theme = useTheme();
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: block.id,
        attributes: { role: "group", tabIndex: -1 },
    });

    const rowCss = css({
        position: "relative",
        opacity: isDragging ? 0.6 : 1,
        transform: CSS.Transform.toString(transform),
        transition,
        cursor: isDragging ? "grabbing" : "grab",
        "& [contenteditable='true']": {
            cursor: isDragging ? "grabbing" : "text",
        },
        "&:hover": {
            backgroundColor: theme.page_background.highlighted,
            outline: `3px solid ${theme.page_background.highlighted}`,
            outlineOffset: 0,
            borderRadius: "var(--rs-radius-small)",
        },
        "&:focus-within": {
            backgroundColor: theme.page_background.highlighted,
            outline: `3px solid ${theme.page_background.highlighted}`,
            outlineOffset: 0,
            borderRadius: "var(--rs-radius-small)",
        },
    });

    return (
        <div ref={setNodeRef} css={rowCss} data-block-id={block.id} {...attributes} {...listeners}>
            <BlockEditor
                block={block}
                previousBlock={previousBlock}
                onChange={onChange}
                controls={controls}
                autoFocus={autoFocus}
                listMarker={listMarker}
            />
        </div>
    );
}
